import { useEffect, useState } from 'react';
import { preloadImage } from '../../helpers/image';

/** URLs we have already started or finished preloading; avoids duplicate Image() requests for the same URL (e.g. when hovering back to the same segment). */
const preloadedUrls = new Set<string>();

/**
 * Returns true once the image at `url` has been loaded and decoded (or was already preloaded).
 * Uses Image.decode() so the thumbnail is only shown when ready to paint without jank.
 * Each URL is only preloaded once per session so the browser can reuse its cache.
 *
 * Only returns true when the currently requested `url` is ready — so a URL change
 * immediately yields false until that new URL finishes loading (avoids painting an
 * unloaded image while still reporting "loaded" from the previous URL).
 */
export function useImagePreload(url?: string): boolean {
  const [loadedUrl, setLoadedUrl] = useState<string | null>(() =>
    url && preloadedUrls.has(url) ? url : null,
  );

  useEffect(() => {
    if (!url) {
      return;
    }
    if (preloadedUrls.has(url)) {
      setLoadedUrl(url);
      return;
    }

    let cancelled = false;
    preloadImage(url, () => {
      preloadedUrls.add(url);
      if (!cancelled) {
        setLoadedUrl(url);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return !!url && loadedUrl === url;
}
